<template>
  <div :class="{ 'flight-details': true, 'is-open': isOpen, 'is-loading': !isLoaded }">
    <div class="flight-details-header">
      <h2 class="flight-details-header-title">
        Flight Details <span v-if="checklistIsDisplayed">/ Checklist</span>

        <d-button
          v-if="!checklistIsDisplayed"
          outline
          size="sm"
          class="ml-4"
          @click.prevent="$router.push('/dashboard/trips/' + parseInt(data.trip_id, 10))">
          Edit schedule
          <i class="material-icons">edit</i>
        </d-button>
      </h2>

      <a
        v-if="!checklistIsDisplayed"
        href="#close"
        class="flight-details-header-close"
        @click.prevent="closeFlightDetails">
        <i class="material-icons">clear</i>
      </a>

      <a
        v-if="checklistIsDisplayed"
        href="#close"
        class="flight-details-header-close"
        @click.prevent="closeChecklist">
        <i class="material-icons">clear</i>
      </a>
    </div>

    <div
      v-if="isLoaded && !checklistIsDisplayed"
      class="flight-details-content pl-3 pr-3 pt-3 pb-3">
      <h4 class="flight-aircraft mb-0">
        {{ data.aircraft_regin_mark }}
      </h4>

      <div class="w-100 mb-2 flight-aircraft-short-info">
        {{ data.departure_date | displayDate }}
        <template v-if="data.departure_airport">{{ data.departure_airport.iata }}</template>
        {{ data.departure_date | displayHour }} - {{ data.arrival_date | displayHour }}
        <template v-if="data.arrival_airport">{{ data.arrival_airport.iata }}</template>
        <template v-if="data.departure_date.split(' ')[0] !== data.arrival_date.split(' ')[0]">
          {{ data.arrival_date | displayDate }}
        </template>
      </div>

      <div
        v-if="(data.slot_arrival_requested_time && data.slot_arrival_granted_time) || (data.slot_departure_requested_time && data.slot_departure_granted_time)"
        class="slot-details pt-2 mb-2">
        <template v-if="data.slot_arrival_requested_time && data.slot_arrival_granted_time">
          ARR: {{ data.slot_arrival_requested_time }} (requested) {{ data.slot_arrival_granted_time }} (granted) <br>
        </template>
        <template v-if="data.slot_departure_requested_time && data.slot_departure_granted_time">
          DEP: {{ data.slot_departure_requested_time }} (requested) {{ data.slot_departure_granted_time }} (granted)
        </template>
      </div>

      <ul class="flight-details-sections">
        <li
          class="flight-details-section"
          @click.prevent="openChecklist">
          <span class="flight-details-section-title">
            Checklist
          </span>

          <d-badge pill :theme="checkedItems === allItems ? 'success' : 'danger'">
            {{ checkedItems }} / {{ allItems }}
          </d-badge>
        </li>
        <li
          class="flight-details-section"
          @click.prevent="showTripDetails()">
          <span class="flight-details-section-title">
            Trip schedule
          </span>
        </li>
        <li
          class="flight-details-section"
          @click.prevent="openFlightForm('permissions')">
          <span class="flight-details-section-title">
            Permits
          </span>

          <d-badge pill :theme="confirmedPermits === allPermits ? 'success' : 'danger'">
            {{ confirmedPermits }} / {{ allPermits }}
          </d-badge>
        </li>
        <li
          class="flight-details-section"
          @click.prevent="openFlightForm('crew')">
          <span class="flight-details-section-title">
            Crew
          </span>
        </li>
        <li
          class="flight-details-section"
          @click.prevent="openFlightForm('passengers')">
          <span class="flight-details-section-title">
            Passengers
          </span>
        </li>
        <li
          class="flight-details-section"
          @click.prevent="openFlightForm('flight_details')">
          <span class="flight-details-section-title">
            Trip brief
          </span>
        </li>
        <li
          class="flight-details-section d-block"
          @click.prevent="toggleWarnings()">
          <div class="flight-details-section-title d-flex">
            <span>
              Airport warnings
            </span>
            <d-badge
              pill
              class="ml-auto"
              :theme="airportWarnings.length ? 'danger' : 'light'">
              {{ airportWarnings.length }}
            </d-badge>
          </div>

          <div
            v-show="showAirportWarnings"
            @click.stop>
            <div
              v-for="(warning, index) of airportWarnings"
              :key="'airport-warning-' + index"
              class="mt-2 airport-warning px-3 py-2">
              <small>
                {{ warning.valid_from | displayFormattedDate }} - {{ warning.valid_to | filterValidTo | displayFormattedDate }}
              </small>

              <div class="align-middle warning-desc">
                <small class="text-intro">
                  <strong
                    :class="'text-' + (warning.severity_level === 'HIGH' ? 'danger' : (warning.severity_level === 'MEDIUM' ? 'warning' : 'secondary'))"
                    @click="$bus.$emit('show-warning-popup', 'edit', false, false, warning.id)"
                    style="cursor: pointer">
                    {{ warning.reference_name }}
                  </strong><br>
                  {{ warning.message }}
                </small>
              </div>
            </div>
          </div>
        </li>
        <li
          class="flight-details-section"
          @click.prevent="openFlightForm('handlers')"
          hidden>
          <span class="flight-details-section-title">
            Departure/arrival handling
          </span>
        </li>
        <li class="flight-details-section">
          <details class="w-100">
            <summary class="flight-details-section-title w-100 d-block">
              PDF Files
            </summary>

            <div>
              <d-button
                class="w-100 mb-2 mt-2"
                @click.prevent="getHandlingRequest('departure')"
                :disabled="!data.is_departure_handling_request">
                DEP handling Request
              </d-button>

              <d-button
                class="w-100 mb-2"
                @click.prevent="getHandlingRequest('arrival')"
                :disabled="!data.is_arrival_handling_request">
                ARR handling Request
              </d-button>

              <div class="gendec-row">
                <d-button
                  outline
                  class="w-100 is-special-button mb-2"
                  :disabled="downloadingGeneralDeclaration === 'departure'"
                  @click.prevent="generateGeneralDeclaration('departure')">
                  <template v-if="downloadingGeneralDeclaration === 'departure'">Downloading...</template>
                  <template v-else>DEP GD</template>
                </d-button>

                <d-button
                  outline
                  class="w-100 is-special-button is-special-button-darker mb-2"
                  :disabled="downloadingGeneralDeclaration === 'both'"
                  @click.prevent="generateGeneralDeclaration('both')">
                  <template v-if="downloadingGeneralDeclaration === 'both'">Downloading...</template>
                  <template v-else>BOTH GD</template>
                </d-button>

                <d-button
                  outline
                  class="w-100 is-special-button mb-2"
                  :disabled="downloadingGeneralDeclaration === 'arrival'"
                  @click.prevent="generateGeneralDeclaration('arrival')">
                  <template v-if="downloadingGeneralDeclaration === 'arrival'">Downloading...</template>
                  <template v-else>ARR GD</template>
                </d-button>
              </div>

              <d-button
                class="w-100 mb-2"
                :disabled="loadingTripBrief"
                @click.prevent="getTripBrief()">
                <template v-if="!loadingTripBrief">Crew Trip Brief</template>
                <template v-else>Generating PDF&hellip;</template>
              </d-button>

              <d-button
                class="w-100 mb-2"
                :disabled="loadingMultisectorTripBrief"
                @click.prevent="getMultisectorTripBrief()">
                <template v-if="!loadingMultisectorTripBrief">Crew Trip Brief (multi)</template>
                <template v-else>Generating PDF&hellip;</template>
              </d-button>

              <d-button
                outline
                class="w-100 mb-2"
                :disabled="!canGenerateAviationReport || downloadingGeneralAviationReport"
                :theme="!canGenerateAviationReport ? 'secondary' : 'primary'"
                @click.prevent="generateGeneralAviationReport">
                <template v-if="downloadingGeneralAviationReport">Downloading...</template>
                <template v-else>General Aviation Report</template>
              </d-button>

              <d-button
                v-if="data.arrival_airport.country_name === 'Israel' || data.departure_airport.country_name === 'Israel'"
                outline
                class="w-100 mb-2"
                :disabled="downloadingASOC"
                theme="secondary"
                @click.prevent="generateASOC">
                <template v-if="downloadingASOC">Downloading...</template>
                <template v-else>Generate ASOC</template>
              </d-button>
            </div>
          </details>
        </li>
        <li class="flight-details-section d-block">
          <details class="w-100">
            <summary class="flight-details-section-title w-100 d-block">
              GAR
            </summary>

            <div>
              <label class="d-block mt-2">Reason for visit:</label>
              <d-form-select
                class="d-block mb-2"
                v-model="reasonVisit"
                :options="[{
                  'text': 'Based',
                  'value': 'Based'
                },{
                  'text': 'Short term visit',
                  'value': 'Short term visit'
                },{
                  'text': 'Maintenance',
                  'value': 'Maintenance'
                },{
                  'text': 'Permanent import',
                  'value': 'Permanent import'
                },{
                  'text': 'Repair',
                  'value': 'Repair'
                }]" />
              <d-button
                class="w-100"
                :disabled="loadingGAR || !reasonVisit"
                @click.prevent="getGAR()">
                <template v-if="!loadingGAR">Generate GAR</template>
                <template v-else>Generating GAR&hellip;</template>
              </d-button>
            </div>
          </details>
        </li>
        <li class="flight-details-section d-block">
          <details class="w-100">
            <summary class="flight-details-section-title w-100 d-block">
              MyHandling pax list
            </summary>
            <div>
              <d-button
                outline
                class="w-100 mt-2"
                :disabled="downloadingPaxExport"
                theme="primary"
                @click.prevent="downloadPaxExport">
                <template v-if="downloadingPaxExport">Downloading...</template>
                <template v-else>Generate PAX Export File</template>
              </d-button>
            </div>
          </details>
        </li>
        <li class="flight-details-section d-block">
          <details class="w-100">
            <summary class="flight-details-section-title w-100 d-block">
              EES/ETIAS
            </summary>
            <div>
              <d-button
                outline
                class="w-100 mt-2"
                :disabled="downloadingEesEtiasExport"
                theme="primary"
                @click.prevent="downloadEesEtiasExport">
                <template v-if="downloadingEesEtiasExport">Downloading...</template>
                <template v-else>Generate EES/ETIAS file</template>
              </d-button>
            </div>
          </details>
        </li>
      </ul>
    </div>

    <div
      v-if="isLoaded && checklistIsDisplayed"
      class="flight-details-content flight-details-content-checklist pl-3 pr-3 pt-0 pb-3">
      <h4 class="flight-aircraft is-sticky mb-3">
        <div class="w-100 d-flex">
          {{ data.aircraft_regin_mark }}

          <d-badge pill :theme="checkedItems === allItems ? 'success' : 'danger'">
            {{ checkedItems }} / {{ allItems }}
          </d-badge>

          <d-button
            theme="success"
            squared
            pill
            @click.prevent="openFlightForm('checklists')"
            class="mt-4"
            style="position: absolute; right: 15px; top: 50px;">
            Add item
          </d-button>
        </div>

        <div class="w-100 flight-aircraft-short-info">
          {{ data.departure_date | displayDate }}
          <template v-if="data.departure_airport">{{ data.departure_airport.iata }}</template>
          {{ data.departure_date | displayHour }} - {{ data.arrival_date | displayHour }}
          <template v-if="data.arrival_airport">{{ data.arrival_airport.iata }}</template>
          <template v-if="data.departure_date.split(' ')[0] !== data.arrival_date.split(' ')[0]">
            {{ data.arrival_date | displayDate }}
          </template>
        </div>

        <div class="flight-aircraft-headlines">
          <span>REQ</span>
          <span>CNF</span>
          <span>N/R</span>
        </div>
      </h4>

      <div
        class="checklist-group"
        v-for="(group, index) of checklistGroups"
        :key="'checklist-group-' + index">
        <strong>
          <template v-if="group.reference_type === 'AIRPORT' && group.reference_id === data.departure_airport.id">
            Departure
          </template>
          <template v-if="group.reference_type === 'AIRPORT' && group.reference_id === data.arrival_airport.id">
            Arrival
          </template>
          {{ group.reference_name }}
        </strong>

        <ol class="checklist-group-list">
          <li
            v-for="(item, subindex) of getChecklistGroupItems(group.reference_id, group.reference_type)"
            :key="'checklist-group-item-' + index + '-' + subindex"
            :class="{ 'checklist-group-item': true, 'is-open': openedItems[item.id] }">
            <div class="checklist-group-item-wrapper">
              <d-form-radio
                v-model="item.state"
                inline
                :name="'state-' + index + '-' + subindex"
                value="required" />
              <d-form-radio
                v-model="item.state"
                inline
                :name="'state-' + index + '-' + subindex"
                value="confirmed" />
              <d-form-radio
                v-model="item.state"
                inline
                :name="'state-' + index + '-' + subindex"
                value="not_required" />
              <d-badge
                class="checklist-group-item-label"
                :theme="(item.state === 'confirmed' || item.state === 'not_required') ? 'success' : ((item.state === 'required') ? 'warning' : 'light')">
                {{ item.name }}
                <i
                  v-if="['required', 'confirmed', 'not_required'].indexOf(item.state) > -1"
                  class="material-icons"
                  @click="resetState(item.id)">
                  clear
                </i>
              </d-badge>

              <d-badge
                class="checklist-item-edit-note"
                :theme="item.notes ? 'primary' : 'light'"
                pill>
                <i
                  v-if="!openedItems[item.id]"
                  class="material-icons"
                  @click="openItem(item.id)">
                  edit
                </i>
              </d-badge>

              <d-badge
                class="checklist-item-edit-note"
                theme="danger"
                pill>
                <i
                  v-if="openedItems[item.id]"
                  class="material-icons"
                  @click="closeItem(item.id)">
                  clear
                </i>
              </d-badge>
            </div>

            <div
              v-if="openedItems[item.id]"
              class="checklist-group-item-notes pb-2 mt-2">
              <d-input
                v-model="item.notes"
                size="sm" />
            </div>
          </li>
        </ol>
      </div>
    </div>

    <d-button
      v-if="isLoaded && checklistIsDisplayed"
      theme="primary"
      squared
      class="checklist-group-save w-100"
      @click.prevent="saveChecklist">
      Save changes
    </d-button>
  </div>
</template>

<script>
import AircraftAdditionalInfo from './../../components/common/AircraftAdditionalInfo';
import DateUtils from './../../utils/DateUtils.js';
import FormUtils from './../../utils/FormUtils.js';
import Vue from 'vue';

export default {
  name: 'calendar-flight-details',
  props: [
    'aircrafts'
  ],
  components: {
    'aircraft-additional-info': AircraftAdditionalInfo
  },
  filters: {
    displayHour (value) {
      if (value) {
        value = value.split(' ')[1];
        value = value.split(':');
        value = [value[0], value[1]].join('');
        return value;
      }

      return '';
    },
    displayDate (value) {
      if (value) {
        let input = new Date(value);

        return [
          input.getDate(),
          DateUtils.getMonthShortName(input.getMonth() + 1),
          input.getFullYear().toString().substr(2, 2)
        ].map(n => n < 10 ? '0' + n : n).join('');
      }

      return '';
    },
    filterValidTo (validToDate) {
      if (validToDate === null) {
        return 'not specified';
      }

      return validToDate;
    },
    displayFormattedDate (value) {
      if (value && value !== 'not specified') {
        let input = new Date(value);
        let hourValue = value.split(' ')[1];
        hourValue = hourValue.split(':');
        let hour = [hourValue[0], hourValue[1]].join('');

        return [
          input.getDate(),
          DateUtils.getMonthShortName(input.getMonth() + 1),
          input.getFullYear().toString().substr(2, 2)
        ].map(n => n < 10 ? '0' + n : n).join('') + ' ' + hour;
      }

      return '';
    }
  },
  computed: {
    isLoaded () {
      return this.isFlightLoaded && this.isChecklistLoaded && this.isAirportWarningsLoaded && this.isPermissionsLoaded;
    },
    checkedItems () {
      return this.checklists.filter(item => item.state === 'confirmed' || item.state === 'not_required').length;
    },
    allItems () {
      return this.checklists.length;
    },
    confirmedPermits () {
      return this.permissions.filter(permission => permission.confirmed).length;
    },
    allPermits () {
      return this.permissions.length;
    },
    checklistGroups () {
      let groups = [...new Set(this.checklists.map(checklistItem => [
        checklistItem.reference_id,
        checklistItem.reference_name,
        checklistItem.reference_type
      ].join('|||')))
      ].map(checklistItem => ({
        reference_id: parseInt(checklistItem.split('|||')[0], 10),
        reference_name: checklistItem.split('|||')[1],
        reference_type: checklistItem.split('|||')[2]
      }));

      groups.sort((itemA, itemB) => {
        if (itemA.reference_type === 'AIRPORT' && itemA.reference_id === this.data.departure_airport.id) {
          return -1;
        }

        if (
          (itemA.reference_type === 'AIRPORT' && itemA.reference_id === this.data.arrival_airport.id) &&
          !(itemB.reference_type === 'AIRPORT' && itemB.reference_id === this.data.departure_airport.id)
        ) {
          return -1;
        }

        if (
          (itemA.reference_type === 'AIRPORT' && itemA.reference_id === this.data.arrival_airport.id) &&
          (itemB.reference_type === 'AIRPORT' && itemB.reference_id === this.data.departure_airport.id)
        ) {
          return 1;
        }

        return itemA.reference_name.localeCompare(itemB.reference_name);
      });

      return groups;
    },
    canGenerateAviationReport () {
      let departureIndex = this.$root.airports.findIndex(airport => airport.id === this.data.departure_airport.id);
      let arrivalIndex = this.$root.airports.findIndex(airport => airport.id === this.data.arrival_airport.id);

      if (this.$root.airports[departureIndex].in_uk || this.$root.airports[arrivalIndex].in_uk) {
        return true;
      }

      return false;
    }
  },
  data () {
    return {
      airportWarnings: [],
      isOpen: false,
      loadError: false,
      isAirportWarningsLoaded: false,
      isFlightLoaded: false,
      isChecklistLoaded: false,
      isPermissionsLoaded: false,
      checklistIsDisplayed: false,
      data: {},
      checklists: [],
      checklistsState: [],
      permissions: [],
      openedItems: {},
      loadingGAR: false,
      loadingTripBrief: false,
      loadingMultisectorTripBrief: false,
      reasonVisit: 'Short term visit',
      showAirportWarnings: false,
      downloadingGeneralAviationReport: false,
      downloadingGeneralDeclaration: false,
      downloadingPaxExport: false,
      downloadingEesEtiasExport: false,
      downloadingASOC: false
    };
  },
  mounted () {
    this.$bus.$on('flight-details-display', this.displayFlightDetails);
    this.$bus.$on('flight-details-reload', this.reloadFlightDetails);
    this.$bus.$on('calendar-flights-update', this.conditionallyReloadFlightDetails);
  },
  methods: {
    displayFlightDetails (id) {
      this.isOpen = true;
      this.isFlightLoaded = false;
      this.isChecklistLoaded = false;
      this.id = id;
      this.loadFlightData();
    },
    closeFlightDetails () {
      this.isOpen = false;
      this.isFlightLoaded = false;
      this.isChecklistLoaded = false;
      this.id = 0;
      this.$bus.$emit('calendar-trip-details-close');
      this.$bus.$emit('flight-details-close');
    },
    conditionallyReloadFlightDetails (source) {
      if (source === 'from-flight-details') {
        return;
      }

      this.reloadFlightDetails(this.data.id);
    },
    reloadFlightDetails (flightID) {
      if (this.isOpen && this.data.id === flightID) {
        this.isFlightLoaded = false;
        this.isChecklistLoaded = false;
        this.loadFlightData(this.data.id);
      }
    },
    openChecklist () {
      this.checklistIsDisplayed = true;
      this.checklistsState = JSON.parse(JSON.stringify(this.checklists));
    },
    closeChecklist () {
      this.checklistIsDisplayed = false;
      this.checklists = JSON.parse(JSON.stringify(this.checklistsState));
    },
    loadFlightData (flightID = false) {
      let self = this;
      let id = this.id;

      if (flightID) {
        id = flightID;
      }

      FormUtils.loadItemData(this, {
        endpointBase: '/trips/flights/item/',
        id: parseInt(id, 10),
        instanceFields: [
          'id',
          'trip_id',
          'aircraft_regin_mark',
          'departure_date',
          'arrival_date',
          'departure_airport',
          'arrival_airport',
          'is_arrival_handling_request',
          'is_departure_handling_request',
          'trip_handling_request_statement',
          'slot_arrival_requested_time',
          'slot_arrival_granted_time',
          'slot_departure_requested_time',
          'slot_departure_granted_time'
        ],
        loadedKey: 'isFlightLoaded',
        successAction () {
          self.loadAdditionalData();
          self.$bus.$emit('calendar-trip-details-change-trip', self.data.trip_id);
        }
      });
    },
    loadAdditionalData () {
      let self = this;

      FormUtils.loadItemData(this, {
        endpointBase: '/calendar/flights/' + parseInt(this.data.id, 10) + '/checklist',
        id: null,
        instanceFields: [],
        loadedKey: 'isChecklistLoaded',
        successAction (response) {
          self.openedItems = {};

          self.checklists = response.map(item => {
            self.openedItems[item.id] = false;
            return item;
          });
        }
      });

      FormUtils.loadItemData(this, {
        endpointBase: '/trips/permissions/items',
        id: null,
        params: {
          pagination: 0,
          where: {
            flight_id: parseInt(self.data.id, 10)
          }
        },
        instanceFields: [],
        loadedKey: 'isPermissionsLoaded',
        successAction (response) {
          self.permissions = response;
        }
      });

      FormUtils.loadItemData(this, {
        endpointBase: '/warnings/items',
        id: null,
        params: {
          pagination: 0,
          where: {
            reference_type: 'FLIGHT',
            reference_id: self.data.id
          }
        },
        instanceFields: [],
        loadedKey: 'isAirportWarningsLoaded',
        successAction (response) {
          self.airportWarnings = response.filter(warning => !warning.is_archived);
        }
      });
    },
    openFlightForm (sectionName) {
      this.$router.push('/dashboard/fligths/' + parseInt(this.data.id, 10) + '/' + sectionName);
    },
    getChecklistGroupItems (groupID, groupType) {
      return this.checklists.filter(item => item.reference_id === groupID && item.reference_type === groupType).sort((itemA, itemB) => itemA.ordering - itemB.ordering);
    },
    showTripDetails () {
      this.$bus.$emit('calendar-trip-details-show', this.data.trip_id);
      this.$bus.$emit('calendar-trip-details-pane-show');
    },
    saveChecklist () {
      let items = {};

      for (let i = 0; i < this.checklists.length; i++) {
        items[this.checklists[i].id] = {
          state: typeof this.checklists[i].state === 'boolean' ? '' : this.checklists[i].state,
          notes: this.checklists[i].notes || ''
        };
      }

      this.isChecklistLoaded = false;

      FormUtils.submitInBackground({
        $http: this.$http,
        formData: {
          items
        },
        endpoint: '/calendar/flights/' + parseInt(this.data.id, 10) + '/checklist/set-state',
        errorTitle: 'An error occurred',
        errorText: 'An error occurred during saving the checklist state. Please try again',
        successAction: () => {
          this.isChecklistLoaded = true;
          this.checklistsState = JSON.parse(JSON.stringify(this.checklists));
          this.$bus.$emit('calendar-flights-update', 'from-flight-details');

          Vue.swal({
            title: 'Success',
            html: 'Checklist state has been successfully saved',
            icon: 'success',
            confirmButtonText: 'OK',
            buttonsStyling: true,
            timer: 1000,
            timerProgressBar: true
          });
        }
      });
    },
    getAircraftByReginMark (reginMark) {
      let foundedAircraft = this.aircrafts.filter(aircraft => aircraft.regin_mark === reginMark);

      if (foundedAircraft.length) {
        return foundedAircraft[0];
      }

      return null;
    },
    resetState (itemID) {
      let index = this.checklists.findIndex(item => item.id === itemID);

      if (index > -1) {
        this.checklists[index].state = '';
      }
    },
    openItem (itemID) {
      Vue.set(this.openedItems, itemID, true);
      this.$forceUpdate();
    },
    closeItem (itemID) {
      Vue.set(this.openedItems, itemID, false);
      this.$forceUpdate();
    },
    getHandlingRequest (requestType) {
      this.$bus.$emit('show-trip-handling-request-popup', {
        id: this.data.id,
        type: requestType,
        text: this.data.trip_handling_request_statement
      });
    },
    getTripBrief () {
      this.loadingTripBrief = true;

      this.$http.post('/trips/flights/get-trip-brief/' + parseInt(this.data.id, 10), {}, {
        responseType: 'blob'
      }).then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        let filename = response.request.getResponseHeader('content-disposition').split('filename=')[1].split(';')[0];
        link.setAttribute('download', filename.replace(/"/gmi, ''));
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        this.loadingTripBrief = false;
      }).catch(error => {
        this.loadingTripBrief = false;
        console.log(error);
        Vue.swal({
          title: 'An error occurred',
          html: 'Please try again',
          icon: 'warning',
          confirmButtonText: 'OK',
          buttonsStyling: true
        });
      });
    },
    getMultisectorTripBrief () {
      this.loadingMultisectorTripBrief = true;

      this.$http.post('/trips/flights/get-multi-sector-trip-brief/' + parseInt(this.data.id, 10), {}, {
        responseType: 'blob'
      }).then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        let filename = response.request.getResponseHeader('content-disposition').split('filename=')[1].split(';')[0];
        link.setAttribute('download', filename.replace(/"/gmi, ''));
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        this.loadingMultisectorTripBrief = false;
      }).catch(error => {
        this.loadingMultisectorTripBrief = false;
        console.log(error);
        Vue.swal({
          title: 'An error occurred',
          html: 'Please try again',
          icon: 'warning',
          confirmButtonText: 'OK',
          buttonsStyling: true
        });
      });
    },
    getGAR () {
      if (!this.reasonVisit) {
        return;
      }

      this.loadingGAR = true;

      this.$http.post('/trips/flights/get-gar/' + parseInt(this.data.id, 10), {
        reason_visit: this.reasonVisit
      }, {
        responseType: 'blob'
      }).then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        let filename = response.request.getResponseHeader('content-disposition').split('filename=')[1].split(';')[0];
        link.setAttribute('download', filename.replace(/"/gmi, ''));
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        this.loadingGAR = false;
      }).catch(error => {
        this.loadingGAR = false;
        console.log(error);
        Vue.swal({
          title: 'An error occurred',
          html: 'Please try again',
          icon: 'warning',
          confirmButtonText: 'OK',
          buttonsStyling: true
        });
      });
    },
    toggleWarnings () {
      this.showAirportWarnings = !this.showAirportWarnings;
    },
    downloadPaxExport () {
      this.downloadingPaxExport = true;

      this.$http.post('/trips/flights/get-pax/' + parseInt(this.data.id, 10), {}, {
        responseType: 'blob'
      }).then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        let filename = response.request.getResponseHeader('content-disposition').split('filename=')[1].split(';')[0];
        link.setAttribute('download', filename.replace(/"/gmi, ''));
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        this.downloadingPaxExport = false;
      }).catch(error => {
        console.log(error);
        Vue.swal({
          title: 'An error occurred',
          html: 'Please try again',
          icon: 'warning',
          confirmButtonText: 'OK',
          buttonsStyling: true
        });

        this.downloadingPaxExport = false;
      });
    },
    downloadEesEtiasExport () {
      this.downloadingEesEtiasExport = true;

      this.$http.post('/trips/flights/get-ees-etias-report/' + parseInt(this.data.id, 10), {}, {
        responseType: 'blob'
      }).then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        let filename = response.request.getResponseHeader('content-disposition').split('filename=')[1].split(';')[0];
        link.setAttribute('download', filename.replace(/"/gmi, ''));
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        this.downloadingEesEtiasExport = false;
      }).catch(error => {
        console.log(error);
        Vue.swal({
          title: 'An error occurred',
          html: 'Please try again',
          icon: 'warning',
          confirmButtonText: 'OK',
          buttonsStyling: true
        });

        this.downloadingEesEtiasExport = false;
      });
    },
    generateGeneralAviationReport () {
      this.downloadingGeneralAviationReport = true;

      this.$http.post('/trips/flights/get-general-aviation-report/' + parseInt(this.data.id, 10), {}, {
        responseType: 'blob'
      }).then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        let filename = response.request.getResponseHeader('content-disposition').split('filename=')[1].split(';')[0];
        link.setAttribute('download', filename.replace(/"/gmi, ''));
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        this.downloadingGeneralAviationReport = false;
      }).catch(error => {
        console.log(error);
        Vue.swal({
          title: 'An error occurred',
          html: 'Please try again',
          icon: 'warning',
          confirmButtonText: 'OK',
          buttonsStyling: true
        });

        this.downloadingGeneralAviationReport = false;
      });
    },
    generateGeneralDeclaration (declarationType) {
      this.downloadingGeneralDeclaration = declarationType;

      this.$http.post('/trips/flights/get-general-declaration/' + parseInt(this.data.id, 10) + '?type=' + declarationType, {}, {
        responseType: 'blob'
      }).then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        let filename = response.request.getResponseHeader('content-disposition').split('filename=')[1].split(';')[0];
        link.setAttribute('download', filename.replace(/"/gmi, ''));
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        this.downloadingGeneralDeclaration = false;
      }).catch(error => {
        console.log(error);
        Vue.swal({
          title: 'An error occurred',
          html: 'Please try again',
          icon: 'warning',
          confirmButtonText: 'OK',
          buttonsStyling: true
        });

        this.downloadingGeneralDeclaration = false;
      });
    },
    generateASOC () {
      this.downloadingASOC = true;

      this.$http.post('/trips/flights/get-asoc/' + parseInt(this.data.id, 10), {}, {
        responseType: 'blob'
      }).then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        let filename = response.request.getResponseHeader('content-disposition').split('filename=')[1].split(';')[0];
        link.setAttribute('download', filename.replace(/"/gmi, ''));
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        this.downloadingASOC = false;
      }).catch(error => {
        console.log(error);
        Vue.swal({
          title: 'An error occurred',
          html: 'Please try again',
          icon: 'warning',
          confirmButtonText: 'OK',
          buttonsStyling: true
        });

        this.downloadingASOC = false;
      });
    }
  },
  beforeDestroy () {
    this.$bus.$off('flight-details-display', this.displayFlightDetails);
    this.$bus.$off('flight-details-reload', this.reloadFlightDetails);
    this.$bus.$off('calendar-flights-update', this.conditionallyReloadFlightDetails);
  }
}
</script>

<style lang="scss" scoped>
.flight-details {
  background: #f5f5f5;
  border-left: 1px solid #ddd;
  height: calc(100vh - 50px);
  position: fixed;
  right: -420px;
  top: 50px;
  transition: all .25s ease-out;
  width: 400px;
  z-index: 100;

  &.is-open {
    right: 0;
  }

  &.is-loading {
    &:after {
      background: rgba(255, 255, 255, .66);
      content: "";
      height: 100%;
      left: 0;
      position: absolute;
      top: 0;
      width: 100%;
    }

    &:before {
      content: "Loading data...";
      left: 50%;
      position: absolute;
      top: 50%;
      transform: translateX(-50%) translateY(-50%);
      z-index: 1;
    }
  }

  .airport-warning {
    background: #fff;
    border: 1px solid #ddd;
  }

  .slot-details {
    border-top: 1px solid #e5e5e5;
  }

  &-header {
    border-bottom: 1px solid #ddd;
    display: flex;
    padding: 10px;
    position: relative;

    &-title {
      font-size: 20px;
      line-height: 1;
      margin: 0;
    }

    &-close {
      border: 1px solid #ccc;
      border-radius: 50%;
      font-size: 16px;
      height: 24px;
      line-height: 24px;
      margin-left: auto;
      position: absolute;
      right: 10px;
      text-align: center;
      top: 50%;
      transform: translateY(-50%);
      width: 24px;

      i {
        position: relative;
        top: 1px;
      }
    }
  }

  &-sections {
    border-top: 1px solid #ddd;
    list-style-type: none;
    padding-left: 0;

    .badge {
      margin-left: auto;
    }
  }

  &-section {
    border-bottom: 1px solid #ddd;
    cursor: pointer;
    display: flex;
    padding: 8px 0;
    width: 100%;

    &.d-block {
      display: block;
    }

    &-title {
      font-size: 18px;
    }
  }

  .checklist-group {
    & > strong {
      background: #e5e5e5;
      display: block;
      font-weight: bold;
      margin: 0 -1rem;
      padding: 5px 1rem;
    }

    &-list {
      list-style-type: none;
      margin: 0;
      padding-left: 0;
    }

    &-item {
      border-top: 1px solid #e5e5e5;
      height: 40px;
      padding: 8px 0;
      position: relative;

      &:first-child {
        border-top: none;
      }

      .custom-radio {
        margin-bottom: 0;
        margin-right: 0;
        padding-left: 24px;
      }

      &.is-open {
        height: 85px;
      }

      &-wrapper {
        display: flex;
      }

      &-label {
        margin-left: 10px;
        overflow: hidden;
        padding-right: 20px;
        position: relative;
        text-align: left;
        text-overflow: ellipsis;
        white-space: nowrap;
        width: 100%;

        i {
          cursor: pointer;
          font-size: 16px;
          position: absolute;
          right: 4px;
          top: 50%;
          transform: translateY(-48%);
        }
      }
    }
  }

  &-content {
    overflow-y: auto;
    max-height: calc(100vh - 105px);
  }

  &-content-checklist {
    max-height: calc(100vh - 124px);
    overflow: auto;

    .flight-aircraft {
      display: flex;
      flex-wrap: wrap;

      &-short-info {
        font-size: 15px;
        font-weight: 500;
      }

      .badge {
        margin-left: auto;
      }

      &.is-sticky {
        background: #f5f5f5;
        border-bottom: 1px solid #ddd;
        margin: -16px -16px 0 -16px;
        padding: 32px 16px 0 16px;
        position: sticky;
        top: -16px;
        z-index: 2;

        & + .checklist-group {
          margin-top: -16px;
        }

        .flight-aircraft-headlines {
          font-size: 10px;
          line-height: 1;
          margin: 5px 0 8px 0;

          & > span {
            display: inline-block;
            text-align: center;
            text-indent: -2px;
            width: 24px;
          }
        }
      }
    }
  }

  .checklist-item-edit-note {
    cursor: pointer;
    margin: 0 0 0 10px;

    i {
      position: relative;
      top: 1px;
      transform: scale(1.25);
    }
  }

  .checklist-group-save {
    bottom: 0;
    left: 0;
    position: absolute;
  }

  .is-special-button {
    background-color: #7D7;
    border-color: #7d7;
    color: #222;

    &:active,
    &:focus,
    &:hover {
      background-color: #5b5;
      border-color: #5b5;
    }

    &.is-special-button-darker {
      background-color: #4a4;
      border-color: #4a4;

      &:active,
      &:focus,
      &:hover {
        background-color: #181;
        border-color: #181;
      }
    }
  }

  details {
    summary {
      position: relative;

      &::after {
        border: 7px solid transparent;
        border-left-color: black;
        content: "";
        display: inline-block;
        position: absolute;
        right: 0;
        top: 5px;
        transform: rotate(0deg);
        transform-origin: 0% 50%;
        transition: transform 0.3s ease;
      }

      &::marker,
      &::-webkit-details-marker {
        display: none;
      }
    }

    &[open] summary::after {
      transform: rotate(90deg);
    }
  }

  .gendec-row {
    display: flex;
    gap: 10px;
  }
}
</style>
